import { useMemo, useState } from "react";
import { Search, X } from "lucide-react";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { USER_ROLE_OPTIONS } from "../constants";
import { useUsers } from "../hooks/useUsers";
// AuthContext.jsx is plain JS, so this import is implicitly typed `any`.
import { useAuth } from "../../../contexts/AuthContext";
import { useUpdateMCPConnection } from "../hooks/useUpdateMCPConnection";
import type {
  MCPSharingUserItem,
  MCPUserRole,
} from "../../../services/agent-tools-service";
import {
  enforceOrganizationFullAccess,
  requiresFullMcpAccess,
} from "../organizationAccess";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  users: MCPSharingUserItem[];
  canManage: boolean;
};

const UserAccessList = ({ connectionId, users, canManage }: Props) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const { data: orgUsers = [] } = useUsers();
  const { userProfile, currentUser } = useAuth();
  const updateMutation = useUpdateMCPConnection();

  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const user of orgUsers) map.set(user.id, user.username);
    return map;
  }, [orgUsers]);

  const orgUserById = useMemo(
    () => new Map(orgUsers.map((user) => [user.id, user])),
    [orgUsers],
  );

  // The signed-in user stays listed so the sharing picture is complete, but
  // their own row is read-only — you can't demote or remove yourself here.
  // Match on email first: the auth profile carries the Keycloak subject id,
  // which is a different uuid from the org member's `user.id`.
  const selfId = useMemo<string | undefined>(() => {
    const email = (userProfile?.email ?? currentUser?.email)?.toLowerCase();
    const byEmail = email
      ? orgUsers.find((user) => user.email?.toLowerCase() === email)
      : undefined;
    return byEmail?.id ?? userProfile?.uid ?? currentUser?.id;
  }, [orgUsers, userProfile, currentUser]);

  const persist = (next: MCPSharingUserItem[]) => {
    if (!canManage) return;
    updateMutation.mutate({
      id: connectionId,
      payload: {
        sharing: {
          scope: "user",
          items: enforceOrganizationFullAccess(next, orgUsers),
        },
      },
    });
  };

  const handleRoleChange = (userId: string, role: MCPUserRole) => {
    if (requiresFullMcpAccess(orgUserById.get(userId))) return;
    persist(users.map((u) => (u.id === userId ? { ...u, role } : u)));
  };

  const handleRemove = (userId: string) => {
    persist(users.filter((u) => u.id !== userId));
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = nameById.get(u.id) ?? u.id;
      return name.toLowerCase().includes(q);
    });
  }, [search, users, nameById]);

  return (
    <div className="min-w-0 overflow-hidden rounded-md px-2">
      <div className="relative flex h-[43px] min-w-0 items-start pe-2">
        <Search
          className="pointer-events-none absolute start-3 top-2.5 size-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          type="text"
          placeholder={t("mcpPage.search")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 bg-input ps-9 pe-3"
        />
      </div>

      {filtered.length === 0 ? (
        <p className="py-3 text-sm text-muted-foreground">
          {users.length === 0
            ? t("mcpPage.noUsersShared")
            : t("mcpPage.noMatchingUsers")}
        </p>
      ) : (
        <ul className="min-w-0">
          {filtered.map((user) => {
            const isSelf = user.id === selfId;
            const mustKeepFullAccess = requiresFullMcpAccess(
              orgUserById.get(user.id),
            );
            const displayName = nameById.get(user.id) ?? user.id;
            return (
              <li
                key={user.id}
                className="flex h-[50px] min-w-0 items-center border-b border-border last:border-b-0"
              >
                <span className="flex min-w-0 flex-1 items-center px-2 text-sm font-medium text-secondary-foreground">
                  <span className="min-w-0 truncate" title={displayName}>
                    {displayName}
                    {isSelf ? ` (${t("mcpPage.you")})` : ""}
                  </span>
                </span>

                <span className="flex min-w-0 flex-1 items-center px-2">
                  <Select
                    value={mustKeepFullAccess ? "owner" : user.role}
                    onValueChange={(role) =>
                      handleRoleChange(user.id, role as MCPUserRole)
                    }
                    disabled={
                      !canManage ||
                      isSelf ||
                      mustKeepFullAccess ||
                      updateMutation.isPending
                    }
                  >
                    <SelectTrigger className="h-9 w-full min-w-0 border-border bg-background text-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {USER_ROLE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {t(`mcpPage.userRoles.${option.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </span>

                <span className="flex w-9 shrink-0 items-center justify-end">
                  <button
                    type="button"
                    onClick={() => handleRemove(user.id)}
                    disabled={!canManage || isSelf || updateMutation.isPending}
                    aria-label={t("mcpPage.actions.remove", {
                      name: displayName,
                    })}
                    className="flex size-9 items-center justify-center rounded-md text-muted-foreground outline-none transition-colors hover:text-destructive focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <X className="size-4" aria-hidden="true" />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default UserAccessList;
